// Display-label helpers for Carrier / CarrierContact enums —
// FE-SCRM-030. Kept in a tiny module so list / detail / modal
// components import the same human-readable mapping.

import type {
  CarrierContactRoleType,
  CarrierContactStatus,
  CarrierStatus,
  CarrierType,
} from "./types";


export const CARRIER_TYPE_LABEL: Record<CarrierType, string> = {
  medical: "Medical",
  dental: "Dental",
  vision: "Vision",
  life: "Life",
  disability: "Disability",
  supplemental: "Supplemental",
  pharmacy: "Pharmacy",
  multi_line: "Multi-line",
};


export const CARRIER_STATUS_LABEL: Record<CarrierStatus, string> = {
  active: "Active",
  inactive: "Inactive",
};


export const CARRIER_CONTACT_ROLE_LABEL: Record<
  CarrierContactRoleType, string
> = {
  sales_rep: "Sales rep",
  account_manager: "Account manager",
  underwriter: "Underwriter",
  implementation: "Implementation",
  claims: "Claims",
  billing: "Billing",
  broker_relations: "Broker relations",
  compliance: "Compliance",
  other: "Other",
};


export const CARRIER_CONTACT_STATUS_LABEL: Record<
  CarrierContactStatus, string
> = {
  active: "Active",
  inactive: "Inactive",
  left_carrier: "Left carrier",
};


/** Render a UTC ISO timestamp as a coarse "N units ago" string for
 * the relationship-health column. Falls back to a short date for
 * old timestamps. Returns ``null`` when the input is missing so the
 * caller can render an em-dash placeholder.
 *
 * Uses ``Date.now()`` so tests can stub the clock when needed; the
 * exact phrasing matches the spec's "2 hours ago / 3 days ago /
 * 2 weeks ago / 3 months ago" cadence.
 */
export function formatRelativeTouchpoint(
  iso: string | null | undefined,
  now: number = Date.now(),
): string | null {
  if (!iso) return null;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return null;
  const diffMs = Math.max(0, now - ts);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day;
  const year = 365 * day;
  if (diffMs < hour) {
    const n = Math.max(1, Math.floor(diffMs / minute));
    return `${n} minute${n === 1 ? "" : "s"} ago`;
  }
  if (diffMs < day) {
    const n = Math.floor(diffMs / hour);
    return `${n} hour${n === 1 ? "" : "s"} ago`;
  }
  if (diffMs < week) {
    const n = Math.floor(diffMs / day);
    return `${n} day${n === 1 ? "" : "s"} ago`;
  }
  if (diffMs < month) {
    const n = Math.floor(diffMs / week);
    return `${n} week${n === 1 ? "" : "s"} ago`;
  }
  if (diffMs < year) {
    const n = Math.floor(diffMs / month);
    return `${n} month${n === 1 ? "" : "s"} ago`;
  }
  const n = Math.floor(diffMs / year);
  return `${n} year${n === 1 ? "" : "s"} ago`;
}


/** Carrier rows older than 90 days render in a muted color so
 * producers spot cold relationships at a glance. */
export const COLD_TOUCHPOINT_DAYS = 90;

export function isTouchpointCold(
  iso: string | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!iso) return false;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return false;
  return now - ts > COLD_TOUCHPOINT_DAYS * 24 * 60 * 60 * 1000;
}


/** Format a US phone number for display. Carrier contacts are
 * almost always US-based reps; international entries pass through
 * unformatted so we don't mangle a "+44 …" the user typed
 * deliberately. The formatter is shape-only — it accepts whatever
 * the user has typed so far (partial numbers stay readable while
 * they type) and produces:
 *
 *   ``""``                                              → ``""``
 *   ``"3"``                                            → ``"3"``
 *   ``"399"``                                          → ``"(399)"``
 *   ``"399494"``                                       → ``"(399) 494"``
 *   ``"3994949999"``                                   → ``"(399) 494-9999"``
 *   ``"13994949999"`` (11-digit, US country code)      → ``"+1 (399) 494-9999"``
 *   anything with non-NANP shape                       → input unchanged
 *
 * Used both at write time (controlled input in the contact modal
 * so the field self-formats as the user types) and at display time
 * in the contacts table.
 */
export function formatUsPhone(raw: string | null | undefined): string {
  if (!raw) return "";
  const trimmed = raw.trim();
  if (!trimmed) return "";
  // International (or anything with non-NANP punctuation) — leave
  // it alone so a user-typed ``+44 20 …`` survives a re-render
  // through the controlled input.
  if (trimmed.startsWith("+") && !trimmed.startsWith("+1")) return trimmed;
  let digits = trimmed.replace(/\D/g, "");
  let prefix = "";
  if (digits.length === 11 && digits.startsWith("1")) {
    prefix = "+1 ";
    digits = digits.slice(1);
  }
  if (digits.length > 10) {
    // Too long to be NANP — pass through so we don't drop digits.
    return trimmed;
  }
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `${prefix}(${digits}`;
  if (digits.length <= 6) {
    return `${prefix}(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return (
    `${prefix}(${digits.slice(0, 3)}) `
    + `${digits.slice(3, 6)}-${digits.slice(6, 10)}`
  );
}
